"use client";

import React, { useEffect, useState } from 'react';
import Breadcrumb from '@/src/components/ui/Breadcrumb';
import { useTranslations } from 'next-intl';
import { ReturnIcon, GavelIcon } from './icons';
import styles from './StaticPage.module.css';

interface Section {
    id: string;
    title: string;
}

export default function ReturnPolicy() {
    const [activeSection, setActiveSection] = useState<string>('digital-products');

    const tCommon = useTranslations('common');
    const t = useTranslations('return');

    const breadcrumbItems = [
        { label: tCommon('breadcrumb.home'), path: '/' },
        { label: tCommon('breadcrumb.returnPolicy'), path: '/return' }
    ];

    const sections: Section[] = [
        { id: t('sections.0.id'), title: t('sections.0.title') },
        { id: t('sections.1.id'), title: t('sections.1.title') },
        { id: t('sections.2.id'), title: t('sections.2.title') },
        { id: t('sections.3.id'), title: t('sections.3.title') },
        { id: t('sections.4.id'), title: t('sections.4.title') },
        { id: t('sections.5.id'), title: t('sections.5.title') }
    ];

    const scrollToSection = (sectionId: string) => {
        const element = document.getElementById(sectionId);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
            setActiveSection(sectionId);
        }
    };

    useEffect(() => {
        const handleScroll = () => {
            const scrollPosition = window.scrollY + 150;
            for (const section of sections) {
                const element = document.getElementById(section.id);
                if (element) {
                    const { offsetTop, offsetHeight } = element;
                    if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
                        setActiveSection(section.id);
                        break;
                    }
                }
            }
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [sections]);

    return (
        <div className={styles.pageWrapper}>
            <div className={styles.breadcrumbBar}>
                <div className={styles.container}><Breadcrumb items={breadcrumbItems} /></div>
            </div>

            <div className={styles.heroSection}>
                <div className={styles.heroInner}>
                    <h1 className={styles.heroTitle}>{t('hero.title')}</h1>
                    <p className={styles.heroSubtitle}>{t('hero.subtitle')}</p>
                    <p className={styles.bodyTextSm} style={{ fontWeight: 600, marginTop: 16 }} dangerouslySetInnerHTML={{ __html: t('hero.effectiveDate') }} />
                </div>
            </div>

            <div className={styles.container}>
                <div className={styles.legalLayout}>
                    <div>
                        <div style={{ marginBottom: 48 }}>
                            <p className={styles.contentText} dangerouslySetInnerHTML={{ __html: t.raw('content.intro') }} />
                        </div>

                        <section id="digital-products" className={styles.contentSection}>
                            <h2 className={styles.sectionTitle}>{t('content.digitalProducts.title')}</h2>
                            <p className={styles.contentText} dangerouslySetInnerHTML={{ __html: t.raw('content.digitalProducts.text') }} />
                        </section>

                        <section id="crypto-payments" className={styles.contentSection}>
                            <h2 className={styles.sectionTitle}>{t('content.cryptoPayments.title')}</h2>
                            <p className={styles.contentText} dangerouslySetInnerHTML={{ __html: t.raw('content.cryptoPayments.text') }} />
                        </section>

                        <section id="exceptions" className={styles.contentSection}>
                            <h2 className={styles.sectionTitle}>{t('content.exceptions.title')}</h2>
                            <p className={styles.contentText}>{t('content.exceptions.text')}</p>
                            <ul className={styles.bulletList}>
                                <li dangerouslySetInnerHTML={{ __html: t.raw('content.exceptions.items.0') }} />
                                <li dangerouslySetInnerHTML={{ __html: t.raw('content.exceptions.items.1') }} />
                                <li dangerouslySetInnerHTML={{ __html: t.raw('content.exceptions.items.2') }} />
                            </ul>
                            <p className={styles.contentText} dangerouslySetInnerHTML={{ __html: t.raw('content.exceptions.note') }} />
                        </section>

                        <section id="no-refunds" className={styles.contentSection}>
                            <h2 className={styles.sectionTitle}>{t('content.noRefunds.title')}</h2>
                            <ul className={styles.bulletList}>
                                <li>{t('content.noRefunds.items.0')}</li>
                                <li>{t('content.noRefunds.items.1')}</li>
                                <li>{t('content.noRefunds.items.2')}</li>
                                <li>{t('content.noRefunds.items.3')}</li>
                                <li>{t('content.noRefunds.items.4')}</li>
                            </ul>
                        </section>

                        <section id="request-review" className={styles.contentSection}>
                            <h2 className={styles.sectionTitle}>{t('content.requestReview.title')}</h2>
                            <p className={styles.contentText} dangerouslySetInnerHTML={{ __html: t.raw('content.requestReview.text') }} />
                            <ol className={styles.numberedList}>
                                <li>{t('content.requestReview.items.0')}</li>
                                <li>{t('content.requestReview.items.1')}</li>
                                <li>{t('content.requestReview.items.2')}</li>
                            </ol>
                        </section>

                        <section id="contact" className={styles.contentSection}>
                            <h2 className={styles.sectionTitle}>{t('content.contact.title')}</h2>
                            <p className={styles.contentText}>
                                {t('content.contact.email')} <a href="mailto:support@aceb.com" style={{ color: 'var(--color-primary)', textDecoration: 'none' }}>support@aceb.com</a>
                            </p>
                        </section>

                        <div className={styles.infoBox} style={{ fontStyle: 'italic' }}>
                            <p>{t('content.disclaimer')}</p>
                        </div>

                        <div className={styles.lastUpdated}>{t('content.lastUpdated')}</div>
                    </div>

                    <div>
                        <div className={styles.tocSidebar}>
                            <h3 className={styles.tocTitle}>
                                <GavelIcon fontSize="small" />
                                {t('toc.title')}
                            </h3>

                            <nav aria-label="table of contents">
                                <ul className={styles.tocList}>
                                    {sections.map((section) => (
                                        <li
                                            key={section.id}
                                            className={`${styles.tocItem} ${activeSection === section.id ? styles.tocItemActive : ''}`}
                                            onClick={() => scrollToSection(section.id)}
                                        >
                                            {section.title}
                                        </li>
                                    ))}
                                </ul>
                            </nav>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
